import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { useBoard } from '@/hooks/useBoard';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { classroomApi } from '@/lib/api/endpoints';
import type { TeacherChallenge } from '@/lib/api/types';
import { routes } from '@/lib/routes';

/** B01-S10 — classroom board challenge (QR + trusted device PIN). */
const ClassroomEntryPage = () => {
  const { t } = useI18n();
  const { notify } = useToast();
  const navigate = useNavigate();
  const { deviceKey, open } = useBoard();

  const [challenge, setChallenge] = useState<TeacherChallenge | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [pin, setPin] = useState('');
  const [room, setRoom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshChallenge = useCallback(async () => {
    setError(null);
    try {
      const next = await classroomApi.createChallenge(room.trim() || undefined);
      setChallenge(next);
      setSecondsLeft(next.ttl_seconds);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    }
  }, [room, t]);

  useEffect(() => {
    void refreshChallenge();
  }, [refreshChallenge]);

  useEffect(() => {
    if (!challenge) return;
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          void refreshChallenge();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [challenge?.challenge_id, refreshChallenge, challenge]);

  const handleConfirm = async (event: FormEvent) => {
    event.preventDefault();
    if (!challenge) return;
    if (!/^\d{6}$/.test(pin)) {
      setError(t('auth.classroom.pinError'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await classroomApi.confirmChallenge({
        challenge_id: challenge.challenge_id,
        qr_token: challenge.qr_payload,
        device_key: deviceKey,
        pin,
      });
      open(session);
      notify(t('auth.classroom.confirmed', { name: session.teacher_display_name }), 'success');
      navigate(routes.classroomToday);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.classroom.denied'));
      setPin('');
      void refreshChallenge();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard title={t('auth.classroom.title')} description={t('auth.classroom.entryText')}>
      <div className="auth-form">
        {error && <p className="field-error">{error}</p>}

        <div className="auth-field">
          <label htmlFor="classroom-room">{t('auth.classroom.room')}</label>
          <input
            id="classroom-room"
            value={room}
            placeholder="305"
            onChange={(event) => setRoom(event.target.value)}
            onBlur={() => void refreshChallenge()}
          />
        </div>

        <div className="classroom-qr-panel" aria-live="polite">
          <p className="classroom-qr-label">{t('auth.classroom.qrLabel')}</p>
          <code className="classroom-qr-code">{challenge?.qr_payload ?? '…'}</code>
          <p className="classroom-qr-ttl">
            {t('auth.classroom.ttl', { seconds: String(secondsLeft) })}
          </p>
        </div>

        <form className="auth-form" onSubmit={handleConfirm}>
          <div className="auth-field">
            <label htmlFor="classroom-pin">{t('auth.classroom.pin')}</label>
            <input
              id="classroom-pin"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
              required
            />
          </div>
          <button type="submit" className="auth-primary" disabled={busy || !challenge}>
            {busy ? t('common.loading') : t('auth.classroom.confirm')}
          </button>
        </form>

        <button type="button" className="auth-link" onClick={() => navigate(routes.login)}>
          {t('auth.backToLogin')}
        </button>
      </div>
    </AuthCard>
  );
};

export default ClassroomEntryPage;
